import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, X } from "lucide-react";

const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    // Check for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdateReady(true);
              setShowUpdate(true);
            }
          });
        });

        // Check for updates on page focus
        const checkForUpdates = () => {
          registration.update();
        };

        window.addEventListener('focus', checkForUpdates);
        
        // Check for updates every 30 seconds
        const interval = setInterval(checkForUpdates, 30000);

        return () => {
          window.removeEventListener('focus', checkForUpdates);
          clearInterval(interval);
        };
      });
    }

    // Listen for version changes from service worker
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'CACHE_UPDATED') {
        setUpdateReady(true);
        setShowUpdate(true);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleUpdate = () => {
    setShowUpdate(false);
    window.location.reload();
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  if (!showUpdate || !updateReady) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999]"
      >
        <div className="bg-gradient-to-br from-card via-primary/5 to-secondary/5 border-2 border-primary/30 rounded-xl p-4 shadow-2xl shadow-primary/50 backdrop-blur-xl max-w-md">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center shadow-lg shadow-primary/50">
                <RefreshCw size={20} className="text-white" />
              </div>
            </div>
            
            <div className="flex-1">
              <h3 className="font-mono font-bold text-white text-sm mb-1">
                Update Available!
              </h3>
              <p className="text-xs text-white/70">
                A new version of Nexi is ready
              </p>
            </div>

            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title="Dismiss"
            >
              <X size={16} className="text-white/60" />
            </button>
          </div>

          <div className="flex gap-2 mt-4">
            <motion.button
              onClick={handleUpdate}
              className="flex-1 bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-lg font-mono text-xs font-bold uppercase tracking-wider shadow-lg shadow-primary/50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Update Now
            </motion.button>
            
            <motion.button
              onClick={handleDismiss}
              className="px-4 py-2 rounded-lg text-white/60 hover:text-white/90 transition-colors font-mono text-xs"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Later
            </motion.button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpdateNotification;
